'use client';

import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toasterApi } from '../toast/ToastProvider';
import {
  ACCEPT_API_DOCS,
  MAX_APPLICATION_DOCS_FILES,
} from '../documents-reports/application-documentation/applicationDocumentation.constants';
import type {
  ApplicationDocumentationController,
  DocumentationDisplayFile,
  DocumentationPersistHandler,
} from '../documents-reports/application-documentation/applicationDocumentation.types';
import {
  formatAcceptedFileTypes,
  mergeDocumentationDialogFiles,
} from '../documents-reports/application-documentation/applicationDocumentationFiles.utils';

export type ApiDocumentationSource = Pick<
  ApplicationDocumentationController,
  | 'apiDocumentationFiles'
  | 'uploadFiles'
  | 'deleteFile'
  | 'isLoadingDocumentation'
>;

export type UseStagedApiDocumentationFilesArgs = {
  applicationId: string | null;
  documentation: ApiDocumentationSource;
  resolveApplicationId?: () => string | null;
  onStagedFilesChange?: (files: File[]) => void;
  onPersistReady?: (handler: DocumentationPersistHandler | null) => void;
};

export const useStagedApiDocumentationFiles = ({
  applicationId,
  documentation,
  resolveApplicationId,
  onStagedFilesChange,
  onPersistReady,
}: UseStagedApiDocumentationFilesArgs) => {
  const t = useTranslations(
    'designSystem.documentsReports.applicationDocumentation',
  );
  const tApiDocumentation = useTranslations(
    'designSystem.applicationGeneral.apiDocumentation',
  );
  const {
    apiDocumentationFiles,
    uploadFiles,
    deleteFile,
    isLoadingDocumentation,
  } = documentation;
  const [stagedFiles, setStagedFiles] = useState<File[]>([]);
  const [stagedDeletedFileIds, setStagedDeletedFileIds] = useState<
    ReadonlySet<string>
  >(() => new Set());
  const [fileUploadKey, setFileUploadKey] = useState(0);
  const stagedFilesRef = useRef<File[]>([]);
  const stagedDeletedFileIdsRef = useRef(stagedDeletedFileIds);
  const resolveApplicationIdRef = useRef(resolveApplicationId);
  const onStagedFilesChangeRef = useRef(onStagedFilesChange);
  const onPersistReadyRef = useRef(onPersistReady);
  const applicationIdRef = useRef(applicationId);
  const uploadFilesRef = useRef(uploadFiles);
  const deleteFileRef = useRef(deleteFile);

  stagedDeletedFileIdsRef.current = stagedDeletedFileIds;
  resolveApplicationIdRef.current = resolveApplicationId;
  onStagedFilesChangeRef.current = onStagedFilesChange;
  onPersistReadyRef.current = onPersistReady;
  applicationIdRef.current = applicationId;
  uploadFilesRef.current = uploadFiles;
  deleteFileRef.current = deleteFile;

  const supportedFileTypesHint = useMemo(() => {
    const formatsHint = t('supportedFileTypes', {
      formats: formatAcceptedFileTypes(ACCEPT_API_DOCS),
    });
    const maxFilesHint = tApiDocumentation('maxFiles', {
      count: MAX_APPLICATION_DOCS_FILES,
    });

    return `${formatsHint}. ${maxFilesHint}.`;
  }, [t, tApiDocumentation]);

  const visiblePersistedFiles = useMemo(
    () =>
      apiDocumentationFiles.filter(
        (file) => !stagedDeletedFileIds.has(file.id),
      ),
    [apiDocumentationFiles, stagedDeletedFileIds],
  );

  const existingFileCount = visiblePersistedFiles.length + stagedFiles.length;

  const visibleFiles = useMemo(
    (): DocumentationDisplayFile[] => [
      ...visiblePersistedFiles.map((file) => ({
        id: file.id,
        name: file.name,
        size: file.size,
      })),
      ...stagedFiles.map((file) => ({
        name: file.name,
        size: file.size,
        lastModified: file.lastModified,
      })),
    ],
    [stagedFiles, visiblePersistedFiles],
  );

  const updateStagedFiles = useCallback((nextFiles: File[]) => {
    stagedFilesRef.current = nextFiles;
    setStagedFiles(nextFiles);
    onStagedFilesChangeRef.current?.(nextFiles);
  }, []);

  const removeFile = useCallback(
    ({ fileIndex }: { fileIndex: number }) => {
      const fileId = visiblePersistedFiles[fileIndex]?.id;

      if (fileId) {
        setStagedDeletedFileIds(
          (previousIds) => new Set([...previousIds, fileId]),
        );

        return;
      }

      const stagedFileIndex = fileIndex - visiblePersistedFiles.length;

      updateStagedFiles(
        stagedFilesRef.current.filter(
          (_, currentIndex) => currentIndex !== stagedFileIndex,
        ),
      );
    },
    [updateStagedFiles, visiblePersistedFiles],
  );

  const persist = useCallback(async () => {
    const filesToUpload = stagedFilesRef.current;
    const fileIdsToDelete = [...stagedDeletedFileIdsRef.current];
    const hasChanges = filesToUpload.length > 0 || fileIdsToDelete.length > 0;

    if (!hasChanges) {
      return true;
    }

    const currentApplicationId = resolveApplicationIdRef.current
      ? resolveApplicationIdRef.current()
      : applicationIdRef.current;

    if (!currentApplicationId) {
      return false;
    }

    try {
      for (const fileId of fileIdsToDelete) {
        await deleteFileRef.current({
          applicationId: currentApplicationId,
          fileId,
        });
      }

      if (filesToUpload.length > 0) {
        await uploadFilesRef.current({
          applicationId: currentApplicationId,
          category: 'api',
          files: filesToUpload,
        });
      }

      stagedFilesRef.current = [];
      setStagedFiles([]);
      setStagedDeletedFileIds(new Set());
      onStagedFilesChangeRef.current?.([]);

      return true;
    } catch (error: unknown) {
      console.error(
        new Error('Failed to upload API documentation files', {
          cause: error instanceof Error ? error : new Error(String(error)),
        }),
      );
      toasterApi.error({
        title: t('errors.failedToUploadFiles'),
        description: t('errors.pleaseTryAgain'),
      });

      return false;
    }
  }, [t]);

  const persistRef = useRef(persist);

  persistRef.current = persist;

  const handleFilesAccept = useCallback(
    ({ files: selectedFiles }: { files: File[] }) => {
      if (selectedFiles.length === 0 || isLoadingDocumentation) {
        return;
      }

      const nextDialogFiles = mergeDocumentationDialogFiles({
        previousFiles: stagedFilesRef.current,
        newFiles: selectedFiles,
        persistedFileCount: visiblePersistedFiles.length,
      });

      if (nextDialogFiles.hasTooManyFiles) {
        toasterApi.error({
          title: t('errors.failedToUploadFiles'),
          description: t('errors.tooManyFiles', {
            count: MAX_APPLICATION_DOCS_FILES,
          }),
        });
      }

      updateStagedFiles(nextDialogFiles.files);
      setFileUploadKey((key) => key + 1);
    },
    [isLoadingDocumentation, t, updateStagedFiles, visiblePersistedFiles],
  );

  useEffect(() => {
    const registerPersist = onPersistReadyRef.current;

    if (!registerPersist) {
      return;
    }

    registerPersist(async () => persistRef.current());

    return () => {
      onPersistReadyRef.current?.(null);
    };
  }, []);

  const hasStagedChanges =
    stagedFiles.length > 0 || stagedDeletedFileIds.size > 0;

  return {
    accept: ACCEPT_API_DOCS,
    visibleFiles,
    existingFileCount,
    isLoadingDocumentation,
    stagedFiles,
    fileUploadKey,
    supportedFileTypesHint,
    handleFilesAccept,
    removeFile,
    persist,
    hasStagedChanges,
  };
};
